/* global __CONFIG__ */
import {
  all,
  take,
  takeLatest,
  select,
  fork,
  call,
  put,
} from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import Pubnub from 'pubnub';

import NewMessageSound from '@coconut/audio/beep.mp3';
import setPageTitleIcon from '@coconut/utils/setPageTitleIcon';

import { TYPE_TO_DETERMINE_PERSONAL_CHANNEL } from '@coconut/constants/pubnubChannel';

import { types } from '../redux/pubnub';
import {
  appendMessage,
  getNewMessageCount,
  newMessage,
  updateLastTutorReadMessageId,
} from '../redux/chatsMessage';
import {
  fetchSelectedQuestion,
  getAasmState,
  getSelectedQuestionID,
} from '../redux/selectQuestion';
import { fetchAnswerStage } from '../redux/answerStage';
import { CONTENT_ACTION, WAITING_ACTION } from '../constants/messageActions';
import { TO_BOTH } from '../constants/pubnubRecipient';
import { TUTOR } from '@coconut/constants/userRole';
import { getPersonalPubnubChannel } from '@coconut/services/redux/app';
import { getRole } from '@coconut/services/redux/profile';
import { CLARIFY } from '../constants/chatActions';
import { CLARIFYING } from '../constants/answerStages';
import { updateStageAndShowChatStageModal } from '../redux/chatModal';
import { hideChatAlertBanner } from '../redux/chatAlertBanner';
import { fetchChats } from '../redux/chatsMessage';

const newMessageAudio = new Audio(NewMessageSound);

const pubnub = new Pubnub({
  subscribeKey: __CONFIG__.PUBNUB_SUBSCRIBE_KEY,
});

function* subscribe(channelName, userID) {
  const personalChannel = yield select(getPersonalPubnubChannel);

  return eventChannel((emit) => {
    pubnub.setUUID(userID);

    // listen to pubnub event.
    pubnub.subscribe({
      channels: [channelName, personalChannel],
    });

    pubnub.addListener({
      message: (msg) => {
        const { message = {} } = msg;

        const parsedMessage = JSON.parse(message);

        emit(parsedMessage);
        if (parsedMessage.from !== parseInt(userID, 10)) {
          newMessageAudio.play();
        }
      },
    });

    return () => {};
  });
}

/*eslint-disable complexity */
function* read(chan, userID) {
  /*
    To avoid we quit this task, we should not do `return`.
    Otherwise, saga may think all tasks has been done and closed.
  */
  while (true) {
    const message = yield take(chan);
    const role = yield select(getRole);
    const { type } = message;

    // According to we have a personal channel and the response structure is quiet different.
    if (type === TYPE_TO_DETERMINE_PERSONAL_CHANNEL) {
      const { data: { message_id: lastTutorReadMessageId } = {} } = message;

      yield put(updateLastTutorReadMessageId(lastTutorReadMessageId));
    } else {
      // normalize message
      const {
        id,
        mType: mtype,
        fullImage: picture_url, // eslint-disable-line camelcase
        text,
        from: snapaskUserId, // eslint-disable-line camelcase
        username,
        question_state: questionState,
        message_action: messageAction,
        recipient,
        timestamp: createdAt,
        system_message_for_tutor: systemMessageForTutor,
        system_message_for_student: systemMessageForStudent,
      } = message;

      const messageId = id || 0;
      const currAasmState = yield select(getAasmState);
      const currQuestionID = yield select(getSelectedQuestionID);

      if (messageAction === CONTENT_ACTION) {
        yield put(fetchChats());
      }

      if (messageAction === CLARIFY && role === TUTOR) {
        yield put(updateStageAndShowChatStageModal(CLARIFYING));
        yield put(hideChatAlertBanner());
      }

      if (messageAction !== CONTENT_ACTION && currQuestionID) {
        yield put(fetchAnswerStage(currQuestionID));
      }

      // update aasmState of current selected question.
      // if current aasmState not equals to next aasmState, refetch the question.
      if (currAasmState !== questionState) {
        yield put(fetchSelectedQuestion(currQuestionID));
      }

      // if "ME" send out the message, pubnub will
      // publish the message to all entities that subscribe to
      // current channel. Hence, "ME" will receive my own message.
      // Thus, we need to filter out my own message.

      // However in accordance with the new best practice
      // framework.

      // 1. We can determine `recipient` to know this message
      // should be send to which role.

      // 2. To prevent duplicated messages, we only append pubnub message
      // which is not from user.

      // 3. if the pubnub message action === WAITING_ACTION,
      // it's a trigger message, which means we should not display it.

      // note:
      // snapask_uid === 0 means system message.
      let description = text;

      if (snapaskUserId === 0 && recipient === TO_BOTH) {
        /*eslint-disable max-depth */
        if (role === TUTOR) {
          description = systemMessageForTutor;
        } else {
          description = systemMessageForStudent;
        }
      }

      if (
        snapaskUserId.toString() !== userID &&
        messageAction !== WAITING_ACTION &&
        (recipient === TO_BOTH || recipient.includes(role))
      ) {
        yield put(newMessage());
        yield put(
          appendMessage({
            message_action: messageAction,
            id: messageId,
            mtype,
            picture_url,
            description,
            snapask_uid: snapaskUserId,
            username,
            created_at: createdAt,
          })
        );

        const messageCount = yield select(getNewMessageCount);
        setPageTitleIcon(messageCount);
      }
    }
  }
}

function* subscribeChannelFlow(action) {
  const {
    payload: { channelName, userID },
  } = action;

  const chan = yield call(subscribe, channelName, userID);

  yield fork(read, chan, userID);
}

function* unsubscribeChannelFlow(action) {
  const {
    payload: { channelName },
  } = action;
  const personalChannel = yield select(getPersonalPubnubChannel);

  pubnub.unsubscribe({
    channels: [channelName, personalChannel],
  });
}

export default function* () {
  yield all([
    takeLatest(types.SUBSCRIBE_CHANNEL, subscribeChannelFlow),
    takeLatest(types.UNSUBSCRIBE_CHANNEL, unsubscribeChannelFlow),
  ]);
}



// WEBPACK FOOTER //
// ./src/scenes/Chat/services/sagas/pubnub.js